import { Table, Tooltip } from 'antd'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import { PerAvatarPlus } from '../../../components/custom_components/AvatarPlus'
import { PerEmpty } from '../../../components/custom_components/Empty'
import { highlightSearchTerm } from '../../../components/custom_components/Search/Search'
import { useAuth } from '../../../context/auth-context'
import { NameDisplayPreference } from '../../../graphql/enum/name-display-preference.enum'
import { EmploymentStatus, EmployeeSearch } from '../../../types'
import { useEmployeeSearchStore } from '../../employees/EmployeeSearch/EmployeeSearchStore'
import classNames from 'classnames'

interface Props {
  data: EmployeeSearch[]
  loading: boolean
  showMoreButtonVisible: boolean
}

export const SearchResultsTable: FC<Props> = ({ data, loading, showMoreButtonVisible }) => {
  const { user } = useAuth()

  const [search, searchLabel] = useEmployeeSearchStore((state) => [state.search, state.searchLabel], shallow)

  const { t } = useTranslation()

  const employeeTableHeader = <Tooltip title={t('employeeList.table.employeeTooltip')}>{t('searchResults.employee')}</Tooltip>

  const columns = [
    {
      key: 'employee',
      title: employeeTableHeader,
      className: 'minWidth-column-xl textAlign-left',
      render: (rowData: EmployeeSearch) => (
        <PerAvatarPlus
          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
          employeeId={rowData.id}
          firstName={rowData.firstName}
          lastName={rowData.lastName}
          linkToProfile={
            rowData.id !== user.employeeId
              ? {
                  resigned: rowData.employmentStatus !== EmploymentStatus.Active,
                }
              : undefined
          }
          tooltip={rowData.id !== user.employeeId ? t('employeeList.table.viewProfile') : undefined}
          searchTerm={search}
        />
      ),
    },
    {
      key: 'company',
      title: t('searchResults.company'),
      className: 'minWidth-column-m textAlign-left',
      render: ({ companyName }) => highlightSearchTerm(companyName, search),
    },
    {
      key: 'bu',
      title: 'BU',
      className: 'minWidth-column-s textAlign-left',
      render: ({ businessUnitName }: EmployeeSearch) => businessUnitName,
    },
    {
      key: 'level',
      title: t('searchResults.level'),
      className: 'minWidth-column-s textAlign-left',
      render: ({ contractLevel }: EmployeeSearch) => contractLevel,
    },
    {
      key: 'status',
      title: t('searchResults.status'),
      className: 'minWidth-column-m textAlign-left',
      render: ({ employmentStatus }) => employmentStatus.charAt(0) + employmentStatus.slice(1).toLocaleLowerCase(),
    },
  ]

  return (
    <Table
      className={classNames('light small scroll-compensation', {
        viewWithInfoBoxAndShowMore: showMoreButtonVisible,
        viewWithInfoBox: !showMoreButtonVisible,
      })}
      dataSource={data}
      rowKey="id"
      columns={columns}
      pagination={false}
      scroll={{ y: true }}
      locale={{
        emptyText: (
          <PerEmpty
            icon="magnifierNo_stroke"
            heading={
              loading
                ? t('searchResults.searching')
                : !!searchLabel.length
                ? t('searchResults.noResults', { value: searchLabel })
                : t('searchResults.noSearchTerm')
            }
            info={loading ? '' : t('searchResults.tryAgain')}
          />
        ),
      }}
    />
  )
}
